
import { Haptics, ImpactStyle } from '@capacitor/haptics';
import { resolveApiBase } from '../config/runtime';

/**
 * BoCapital Capacitor Bridge — TypeScript port
 * No-op in browser; registers for push in native context.
 */

interface CapacitorWindow extends Window {
  Capacitor?: {
    isNativePlatform?: () => boolean;
    getPlatform?: () => 'ios' | 'android' | 'web';
    Plugins?: Record<string, any>;
  };
  BOC_API_BASE?: string;
}

const win = (typeof window !== 'undefined' ? window : {}) as CapacitorWindow;

export const isCapacitor =
  !!win.Capacitor?.isNativePlatform?.();

export const platform = isCapacitor
  ? win.Capacitor!.getPlatform!()
  : 'web';

function getPlugin(name: string) {
  return win.Capacitor?.Plugins?.[name];
}

/** Trigger a light impact on supported native devices. */
export async function triggerHapticImpact(): Promise<void> {
  if (!isCapacitor) return;

  try {
    await Haptics.impact({ style: ImpactStyle.Light });
  } catch {
    // Ignore unsupported/permission-related failures; haptics are best-effort.
  }
}

/** Register for push notifications (native only) */
export async function registerPush(): Promise<void> {
  if (!isCapacitor) return;

  const PushNotifications = getPlugin('PushNotifications');
  if (!PushNotifications) return;

  let perm = await PushNotifications.checkPermissions();
  if (perm.receive === 'prompt') {
    perm = await PushNotifications.requestPermissions();
  }
  if (perm.receive !== 'granted') return;

  await PushNotifications.register();

  PushNotifications.addListener('registration', async (token: { value: string }) => {
    try {
      const apiBase = resolveApiBase();
      if (!apiBase) return;
      await fetch(`${apiBase}/api/push/register`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'same-origin',
        body: JSON.stringify({ token: token.value, platform, app_version: '6.0.0' }),
      });
    } catch { /* silent */ }
  });

  PushNotifications.addListener('pushNotificationReceived', (notification: any) => {
    // Foreground notification — dispatch custom event for Toast component
    window.dispatchEvent(
      new CustomEvent('bocapital:push', { detail: notification }),
    );
  });

  PushNotifications.addListener('pushNotificationActionPerformed', (action: any) => {
    // Notification tap — deep link
    const route = action?.notification?.data?.route;
    if (route) {
      window.dispatchEvent(
        new CustomEvent('bocapital:deeplink', { detail: { route } }),
      );
    }
  });
}
